/**
 * @class Handles containers loaded buy init scene. other scenes collect their geographical data from this manager.
 */

import { differenceInMilliseconds } from 'date-fns';
import { Vector3 } from 'three';

import {
  PointCloudModel,
  TiledModel,
  TimelineContainerModel,
} from '../../../typings/api/skymap/rest/v0/.common';
import { formattedDate } from '../utils/dateUtils';
import { isDefined } from '../utils/variables';
import { OrthotileData, PointcloudData, ScanData } from './project-geodata-manager';

export type TimelineContainer = Omit<
  TimelineContainerModel,
  'collectionDate' | 'comment' | 'scan' | 'orthotile' | 'pointCloud' | 'tiledModelId'
> & {
  containerIndex: number;
  collectionDate: Date;
  geodataId?: string;
  displayDate: string;
  comment: string;
  scan?: string;
  orthotile?: string;
  pointCloud?: string;
  tiledModelId?: string;
  tiledModelObject?: TiledModel;
  scadData?: ScanData;
  orthoTileData?: OrthotileData;
  pointCloudObject?: PointCloudModel;
  legacyPointCloudData?: PointcloudData;
};

/** Whether the timeline container has data to show: a tiled model, scan, point cloud or orthophoto. */
export const hasContainerData = (container: TimelineContainer) =>
  !!(container.scan || container.orthotile || container.pointCloud || container.tiledModelId);

/**
 * The origo of the container's data in project coordinates: the tiled model's or scan's, otherwise
 * the point cloud's, otherwise the orthophoto's. None for a container without data.
 */
export function getContainerOrigo(container: TimelineContainer): Vector3 | undefined {
  const origo =
    (container.tiledModelId || container.scan ? container.tiledModelOrigo : undefined) ??
    (container.pointCloud ? container.pointCloudOrigo : undefined) ??
    (container.orthotile ? container.orthotileOrigo : undefined);
  return origo && new Vector3(origo[0] ?? 0, origo[1] ?? 0, origo[2] ?? 0);
}

export class TimelineManager {
  static sortContainers(timelineContainers: TimelineContainerModel[]): TimelineContainer[] {
    return timelineContainers
      .sort((a: TimelineContainerModel, b: TimelineContainerModel) => {
        const i = differenceInMilliseconds(b.collectionDate, a.collectionDate);
        return i === 0 ? (a.id > b.id ? -1 : 1) : i;
      })
      .map((container, index, arr) => ({
        ...container,
        geodataId: container.geodataId,
        displayDate: formattedDate(container.collectionDate),
        collectionDate: new Date(container.collectionDate),
        scan: container.scan ?? undefined,
        orthotile: container.orthotile ?? undefined,
        pointCloud: container.pointCloud ?? undefined,
        tiledModelId: container.tiledModelId ?? undefined,
        comment: container.comment ?? '',
        containerIndex: arr.length - index,
      }));
  }
  private selectedIndex: number;
  private containers: TimelineContainer[];

  constructor(containers: TimelineContainerModel[]) {
    this.selectedIndex = containers.length > 0 ? 0 : -1;
    this.containers = TimelineManager.sortContainers(containers);
  }

  generateNativeContiners(): any {
    return this.containers.map((container, index) => {
      return {
        index,
        comment: container.comment || '',
        date: container.collectionDate,
        scan: isDefined(container.scan) || isDefined(container.tiledModelId),
        orthotile: !!container.orthotile,
        pointCloud: !!container.pointCloud,
      };
    });
  }

  pushSelectedIndex() {
    // @ts-ignore
    window.iosCallbacks && window.iosCallbacks.pushTimelineContainerSelection(this.selectedIndex);
  }

  /**
   *  Returns the latest container
   */
  getLatestContainer(): TimelineContainer | undefined {
    const index = this.getLatestValidIndex(0);
    return index !== -1 ? this.containers[index] : undefined;
  }

  /**
   *  Returns the latest valid container
   *  (should have either orthotile or scan)
   */
  getLatestValidIndex(index = 0): number {
    while (
      !(this.containers[index] && hasContainerData(this.containers[index])) &&
      index <= this.containers.length
    ) {
      index++;
    }
    return index >= this.containers.length ? -1 : index;
  }

  /**
   * Returns the container with data closest in time to the given one, which is the container itself
   * if it has data. Of two as close, the later one.
   */
  getClosestContainerWithData(container: TimelineContainer): TimelineContainer | undefined {
    const distance = (other: TimelineContainer) =>
      Math.abs(other.collectionDate.getTime() - container.collectionDate.getTime());
    // Newest first, so the later of two as close comes first
    return this.containers
      .filter(hasContainerData)
      .reduce<
        TimelineContainer | undefined
      >((closest, other) => (!closest || distance(other) < distance(closest) ? other : closest), undefined);
  }

  getContainerById(timelineContainerId: string): TimelineContainer | undefined {
    return this.containers.find((container) => container.id === timelineContainerId);
  }

  selectById(timelineContainerId: string): void {
    const index = this.containers.findIndex((container) => container.id === timelineContainerId);
    this.selectedIndex = index !== -1 ? index : 0;
  }

  current(): TimelineContainer | undefined {
    return this.containers && this.containers[this.selectedIndex];
  }

  getList(): TimelineContainer[] {
    return this.containers;
  }
}
